import React from 'react'
import { Helmet } from 'react-helmet'
import utils from '../utils'
import { FaMapMarkerAlt } from 'react-icons/fa'
import { FaEnvelope, FaPhone } from 'react-icons/fa6'
import {
    FaFileContract,
    FaUserCheck,
    FaMoneyBillWave,
    FaBan,
    FaGavel,
    FaStethoscope,
    FaHeartPulse,
    FaShield
} from 'react-icons/fa6';
import { FaExclamationTriangle } from "react-icons/fa";

function TermsPage(props) {
    const url = window.location.href
    return (
        <div>
            <Helmet>
                <title>Terms & Conditions - {utils.siteName}</title>
                <link rel="canonical" href={url} />
                <meta
                    name="description"
                    content="HealthConnect Terms of Service. Read our terms and conditions for using our home healthcare platform, including user responsibilities, payments, and legal agreements."
                />
                <meta
                    property="og:title"
                    content="Terms of Service - HealthConnect"
                />
                <meta
                    property="og:description"
                    content="Understanding your rights and responsibilities when using HealthConnect home healthcare services."
                />
                <meta
                    property="og:type"
                    content="website"
                />
                <meta
                    property="og:url"
                    content={url}
                />
                <meta
                    name="keywords"
                    content="terms of service, terms and conditions, healthcare platform, Kenya, HealthConnect, user agreement"
                />
            </Helmet>
            <section className="px-6 md:px-12 py-5 bg-base-100 relative overflow-hidden -mt-4">
                <div className="absolute -top-24 -right-24 size-96 bg-primary/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -left-24 size-96 bg-blue-400/10 rounded-full blur-3xl"></div>

                <div className="max-w-5xl mx-auto relative z-10">
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                        <span className="size-2 rounded-full bg-primary mr-2 animate-pulse"></span>
                        Terms of Service
                    </span>

                    <h2 className="text-3xl md:text-5xl font-black text-text-main dark:text-white leading-[1.1] tracking-tight mb-8">
                        Legal <span className="text-primary">Agreement</span>
                    </h2>

                    <p className="text-lg text-text-secondary dark:text-slate-400 font-light max-w-2xl leading-relaxed">
                        Please read these terms carefully before using HealthConnect. By accessing our platform, you agree to be bound by these terms and conditions.
                    </p>
                </div>
            </section>
            <section className="py-16 px-6">
                <div className="">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-black text-text-main dark:text-white mb-4">
                            Key <span className="text-primary">Terms</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                            <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                <FaUserCheck className="text-3xl" />
                            </div>
                            <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">User Eligibility</h3>
                            <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed">
                                You must be 18 years or older to use HealthConnect. Parents/guardians must create accounts for minors.
                            </p>
                        </div>

                        <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                            <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                <FaFileContract className="text-3xl" />
                            </div>
                            <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Binding Agreement</h3>
                            <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed">
                                By using our platform, you agree to these terms. If you disagree, please do not use our services.
                            </p>
                        </div>

                        <div className="group bg-white dark:bg-slate-900 p-6 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm hover:shadow-card-hover hover:border-primary/30 transition-all duration-300">
                            <div className="size-14 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                                <FaShield className="text-3xl" />
                            </div>
                            <h3 className="text-xl font-bold text-text-main dark:text-white mb-3">Compliance</h3>
                            <p className="text-secondary dark:text-slate-400 text-sm leading-relaxed">
                                We comply with Kenya Data Protection Act, 2019 and healthcare regulations.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 bg-base-100">
                <div className="max-w-4xl mx-auto space-y-12">

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaFileContract className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">1. Acceptance of Terms</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>Welcome to HealthConnect. These Terms of Service govern your use of our platform and services:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>By accessing or using HealthConnect, you agree to be bound by these terms</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>If you disagree with any part of these terms, you may not access our services</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>These terms apply to all users, including patients, nurses, and visitors</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>We reserve the right to modify these terms at any time with notice</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaUserCheck className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">2. Eligibility & Account Registration</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>To use HealthConnect, you must meet these requirements:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Age Requirement:</strong> You must be at least 18 years old</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Account Accuracy:</strong> Provide accurate, current, and complete information</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Account Security:</strong> Maintain confidentiality of your login credentials</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>One Account:</strong> Each user may maintain only one account</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Minors:</strong> Parents/guardians must create and manage accounts for patients under 18</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-accent/20 text-accent flex items-center justify-center">
                                <FaStethoscope className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">3. Service Description</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>HealthConnect provides a platform connecting patients with verified nurses:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Platform Role:</strong> We facilitate connections between patients and healthcare providers</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Not a Provider:</strong> HealthConnect is not a healthcare provider and does not provide medical services directly</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Nurse Independence:</strong> Nurses are independent contractors, not employees of HealthConnect</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Service Scope:</strong> Includes nursing care, medical procedures, health monitoring, and related services</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Availability:</strong> Services subject to nurse availability in your area</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaHeartPulse className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">4. Patient Responsibilities</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>As a patient using HealthConnect, you agree to:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Provide accurate and complete health information for proper nurse matching</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Disclose all relevant medical conditions, allergies, and medications</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Ensure a safe environment for the nurse to provide care</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Be available at the scheduled time or provide 24-hour notice for cancellations</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Follow nurse instructions and medical advice provided during service</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Seek emergency medical care when needed (call 999 or visit nearest hospital)</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-success/20 text-success flex items-center justify-center">
                                <FaUserCheck className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">5. Nurse Requirements</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>To join HealthConnect as a nurse, you must:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Hold a valid nursing license from the Nursing Council of Kenya</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Provide proof of identity, qualifications, and professional registration</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Pass background checks and credential verification</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Maintain active professional liability insurance</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Complete HealthConnect onboarding and training</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Adhere to professional standards and code of conduct</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-success font-bold">•</span>
                                    <span>Keep profile information current and accurate</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaMoneyBillWave className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">6. Payments & Refunds</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>Our payment and refund policies:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Pricing:</strong> All prices are displayed in Kenyan Shillings (KES) and include service fees</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Payment Methods:</strong> We accept M-Pesa, credit/debit cards, and bank transfers</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Payment Timing:</strong> Payment is required before service commencement unless otherwise agreed</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Cancellation (Patient):</strong> Free cancellation 24+ hours before; 50% fee for 12-24 hours; full charge for &lt;12 hours</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Cancellation (Nurse):</strong> Full refund if nurse cancels; we will arrange replacement</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Refund Processing:</strong> Approved refunds processed within 5-7 business days</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Price Changes:</strong> We reserve the right to modify prices with notice</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-warning/20 text-warning flex items-center justify-center">
                                <FaExclamationTriangle className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">7. Medical Disclaimer</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p className="font-bold text-warning">IMPORTANT - PLEASE READ CAREFULLY:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-warning font-bold">•</span>
                                    <span><strong>Not Emergency Services:</strong> HealthConnect is NOT for medical emergencies. Call 999 or go to nearest hospital for emergencies</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-warning font-bold">•</span>
                                    <span><strong>No Diagnosis:</strong> Our platform does not provide medical diagnoses. Nurses provide care based on existing medical instructions</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-warning font-bold">•</span>
                                    <span><strong>Professional Judgment:</strong> Nurses may decline services they deem unsafe or outside their scope</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-warning font-bold">•</span>
                                    <span><strong>Follow-Up Care:</strong> Home care does not replace regular hospital visits or specialist consultations</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-warning font-bold">•</span>
                                    <span><strong>Medication:</strong> Nurses administer medication as prescribed by licensed physicians only</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-warning font-bold">•</span>
                                    <span><strong>Outcomes:</strong> We do not guarantee specific medical outcomes or recovery times</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaShield className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">8. Limitation of Liability</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>To the maximum extent permitted by Kenyan law:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>HealthConnect is not liable for any medical complications arising from services provided</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>We are not responsible for nurse actions or omissions during service delivery</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Maximum liability is limited to the amount paid for the specific service</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>We are not liable for indirect, incidental, or consequential damages</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Platform outages, technical issues, or service interruptions are not grounds for liability claims</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Nurses carry their own professional liability insurance for malpractice claims</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-error/20 text-error flex items-center justify-center">
                                <FaBan className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">9. Prohibited Conduct</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>You agree NOT to:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-error font-bold">•</span>
                                    <span>Use the platform for any illegal or unauthorized purpose</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-error font-bold">•</span>
                                    <span>Provide false, misleading, or inaccurate information</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-error font-bold">•</span>
                                    <span>Harass, abuse, or threaten nurses or other users</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-error font-bold">•</span>
                                    <span>Request services outside the nurse's scope of practice</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-error font-bold">•</span>
                                    <span>Attempt to bypass platform fees or arrange off-platform payments</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-error font-bold">•</span>
                                    <span>Share your account credentials with others</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-error font-bold">•</span>
                                    <span>Use automated systems to access or interact with the platform</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-error font-bold">•</span>
                                    <span>Violate any applicable laws or regulations</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaBan className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">10. Termination</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>We reserve the right to suspend or terminate accounts:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>By You:</strong> You may close your account at any time through app settings or by contacting support</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>By Us:</strong> We may suspend or terminate accounts for violations of these terms</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Notice:</strong> We will provide notice when possible, but may terminate immediately for serious violations</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Effect:</strong> Upon termination, your right to use the platform ceases immediately</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span><strong>Outstanding Obligations:</strong> Termination does not relieve you of payment obligations or liability</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-accent/20 text-accent flex items-center justify-center">
                                <FaGavel className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">11. Governing Law & Disputes</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>Legal jurisdiction and dispute resolution:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Governing Law:</strong> These terms are governed by the laws of Kenya</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Jurisdiction:</strong> Courts of Nairobi, Kenya have exclusive jurisdiction</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Dispute Resolution:</strong> We encourage amicable resolution through direct communication first</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Mediation:</strong> Disputes may be referred to mediation before court proceedings</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-accent font-bold">•</span>
                                    <span><strong>Class Actions:</strong> You agree not to participate in class action lawsuits against HealthConnect</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaFileContract className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">12. Changes to Terms</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>We may modify these terms:</p>
                            <ul className="space-y-2 ml-4">
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Updates will be posted on this page with a new "Last Updated" date</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Material changes will be communicated via email or app notification at least 7 days before effective date</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>Continued use after changes constitutes acceptance of new terms</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-primary font-bold">•</span>
                                    <span>If you disagree with changes, you must stop using the platform</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="group bg-white dark:bg-slate-900 p-8 rounded-2xl border border-border-color dark:border-slate-800 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="size-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center">
                                <FaEnvelope className="text-2xl" />
                            </div>
                            <h3 className="text-2xl font-black text-text-main dark:text-white">13. Contact Information</h3>
                        </div>
                        <div className="space-y-4 text-secondary dark:text-slate-400">
                            <p>For questions about these Terms of Service:</p>
                            <div className="grid md:grid-cols-3 gap-4 mt-6">
                                <a href="mailto:support@healthconnect.co.ke" className="flex items-center gap-3 p-4 rounded-xl bg-base-200 hover:bg-primary/10 transition-colors">
                                    <FaEnvelope className="text-primary text-xl" />
                                    <div>
                                        <p className="font-bold text-text-main dark:text-white text-sm">Email</p>
                                        <p className="text-xs">support@healthconnect.co.ke</p>
                                    </div>
                                </a>
                                <a href="tel:+254700000000" className="flex items-center gap-3 p-4 rounded-xl bg-base-200 hover:bg-primary/10 transition-colors">
                                    <FaPhone className="text-primary text-xl" />
                                    <div>
                                        <p className="font-bold text-text-main dark:text-white text-sm">Phone</p>
                                        <p className="text-xs">+254 700 000 000</p>
                                    </div>
                                </a>
                                <div className="flex items-center gap-3 p-4 rounded-xl bg-base-200">
                                    <FaMapMarkerAlt className="text-primary text-xl" />
                                    <div>
                                        <p className="font-bold text-text-main dark:text-white text-sm">Location</p>
                                        <p className="text-xs">Nairobi, Kenya</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>
            <section className="py-12 px-6 bg-base-100">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-primary/5 dark:bg-slate-800/50 rounded-2xl p-6 border border-primary/20">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <p className="text-sm text-secondary dark:text-slate-400 mb-1">Last Updated</p>
                                <p className="font-bold text-text-main dark:text-white">January 13, 2026</p>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm">
                                <a href="mailto:support@healthconnect.co.ke" className="flex items-center gap-2 text-primary font-bold hover:underline">
                                    <FaEnvelope /> support@healthconnect.co.ke
                                </a>
                                <a href="tel:+254700000000" className="flex items-center gap-2 text-primary font-bold hover:underline">
                                    <FaPhone /> +254 700 000 000
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="py-12 px-6 bg-base-200/50">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-primary rounded-2xl p-8 text-center">
                        <h3 className="text-2xl font-black text-white mb-4">By Using HealthConnect</h3>
                        <p className="text-white/80 text-lg mb-0">
                            You acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                            If you do not agree, please do not use our services.
                        </p>
                    </div>
                </div>
            </section>

        </div>
    )
}

export default TermsPage